import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import styles from "./PassportRecordRequirementTable.module.css";
import DashboardSortTableHeader from "components/DashboardSortTableHeader";
import PassportRecordRequirementItem from "components/PassportRecordRequirementItem";
import { DashboardItemsPerPage } from "components/DashboardItemsPerPage";
import DashboardSearch from "components/DashboardSearch";
import DashboardTablePagination from "components/DashboardTablePagination";
import { Loading } from "@cpnw/ui";

export default function PassportRecordRequirementTable({
  items,
  onItemClick,
  isLoading,
}) {
  // console.log(items);
  //sort header directions
  const [reqNameSortDir, setReqNameSortDir] = useState("");
  const [freqSortDir, setFreqSortDir] = useState("");
  const [typeSortDir, setTypeSortDir] = useState("");
  const [statSortDir, setStatSortDir] = useState("");
  const [dueSortDir, setDueSortDir] = useState("");
  const [expirSortDir, setExpirSortDir] = useState("");
  const [revSortDir, setRevSortDir] = useState("");
  const [scoreSortDir, setScoreSortDir] = useState("");
  const [categorySortDir, setCategorySortDir] = useState("");

  //items per page limit and options
  const [limit, setLimit] = useState(10);
  const [limitOptions] = useState([5, 10, 20, 50, 100, 1000]);

  //search state, and minimum characters
  const [searchValue, setSearchValue] = useState("");
  const [minChar] = useState(2);

  //current page for pagiation
  const [currentPage, setCurrentPage] = useState(1);

  //items after sorting, array of key value pairs for sort directions
  const [sortedItems, setSortedItems] = useState(items);
  const [activeSort, setActiveSort] = useState(null);

  useEffect(() => {
    if (items && items.length > 0) {
      setSortedItems(items); // DO NOT sort
      setActiveSort(null);
      resetAllSortDirs();
    }
  }, [items]);

  const handleLimitChange = (value) => {
    setLimit(value);
  };

  const handleSearch = (value) => {
    const isNumeric = value !== "" && !isNaN(value);

    setSearchValue(value); // ✅ always keep in sync

    if (value.length >= minChar || isNumeric) {
      preformSearch(items, value);
      setCurrentPage(1);
    } else {
      setSortedItems(items);
      setCurrentPage(1);
    }

    resetAllSortDirs();
    setActiveSort(null);
  };

  //search function
  const preformSearch = (itemsArray, searchChar) => {
    const lowerSearch = searchChar.toLowerCase();

    const results = itemsArray.filter((item) => {
      return Object.entries(item).some(([key, value]) => {
        if (value == null) return false;

        // ✅ FIX: score should be exact numeric match
        if (key === "score") {
          if (isNaN(searchChar)) return false;
          return Number(value) === Number(searchChar);
        }

        // default string search
        return value
          .toString()
          .toLowerCase()
          .includes(lowerSearch);
      });
    });

    setSortedItems(results);
  };


  //#region sort directions
  const handleReqNameSortDir = (value) => {
    resetAllSortDirs();
    setReqNameSortDir(value);
    const sort = { header: "reqName", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleFreqSortDir = (value) => {
    resetAllSortDirs();
    setFreqSortDir(value);
    const sort = { header: "freq", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleTypeSortDir = (value) => {
    resetAllSortDirs();
    setTypeSortDir(value);
    const sort = { header: "type", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleStatSortDir = (value) => {
    resetAllSortDirs();
    setStatSortDir(value);
    const sort = { header: "status", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleDueSortDir = (value) => {
    resetAllSortDirs();
    setDueSortDir(value);
    const sort = { header: "dueDate", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleExpirSortDir = (value) => {
    resetAllSortDirs();
    setExpirSortDir(value);
    const sort = { header: "expiration", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleRevSortDir = (value) => {
    resetAllSortDirs();
    setRevSortDir(value);
    const sort = { header: "reviewer", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleScoreSortDir = (value) => {
    resetAllSortDirs();
    setScoreSortDir(value);
    const sort = { header: "score", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };
  const handleCategorySortDir = (value) => {
    resetAllSortDirs();
    setCategorySortDir(value);
    const sort = { header: "createdByTypeContact", direction: value };
    setActiveSort(sort);
    handleSortItems(sort);
  };

  const handleSortItems = ({ header, direction }) => {
    if (!header) {
      setSortedItems(items);
      return;
    }
    const baseItems = searchValue.length >= minChar ? sortedItems : items;
    const itemsToSort = [...baseItems];

    itemsToSort.sort((a, b) => {
      let aVal = a[header];
      let bVal = b[header];
      let comparison = 0;

      if (header === "expiration") {
        const aHasDate = aVal != null && aVal !== "";
        const bHasDate = bVal != null && bVal !== "";

        if (aHasDate && bHasDate) {
          comparison = new Date(aVal) - new Date(bVal);
        } else if (aHasDate && !bHasDate) {
          comparison = -1;
        } else if (!aHasDate && bHasDate) {
          comparison = 1;
        } else {
          comparison = a.reqName.localeCompare(b.reqName);
        }
      } else if (header === "score") {
        comparison = (Number(aVal) || 0) - (Number(bVal) || 0);
      } else {
        if (aVal == null || bVal == null) return 0;

        if (!isNaN(aVal) && !isNaN(bVal)) {
          comparison = Number(aVal) - Number(bVal);
        } else if (!isNaN(Date.parse(aVal)) && !isNaN(Date.parse(bVal))) {
          comparison = new Date(aVal) - new Date(bVal);
        } else {
          comparison = String(aVal).localeCompare(String(bVal));
        }
      }

      return direction === "asc" ? comparison : -comparison;
    });

    setSortedItems(itemsToSort);
  };


  //#endregion end sort directions

  const resetAllSortDirs = () => {
    setReqNameSortDir("");
    setFreqSortDir("");
    setTypeSortDir("");
    setStatSortDir("");
    setDueSortDir("");
    setExpirSortDir("");
    setRevSortDir("");
    setScoreSortDir("");
    setCategorySortDir("");
  };

  //items to render based on search, sort, pagination, and limit
  let itemsToDisplay = sortedItems.slice(
    (currentPage - 1) * limit,
    currentPage * limit
  );

  return (
    <>
      <div className={styles.search_ipp}>
        <div>
          <DashboardItemsPerPage
            disabled={false}
            limit={limit}
            limitOptions={limitOptions}
            onLimitChange={handleLimitChange}
          />
        </div>
        <div className={styles.search}>
          <DashboardSearch
            onInput={(value) => handleSearch(value)}
            searchDelay={1000}
            minChar={minChar}
          />
        </div>
      </div>
      <div className={styles.table_container}>
        <div className={styles.table_headers}>
          <div className={styles.header}>
            <DashboardSortTableHeader
              //key here forces the component to re-render (fixes bug where the user cannot
              // click the same button, after searching.) e.g. (sort, then search, then click same
              // sort buttons)
              key={`reqName-${reqNameSortDir}`}
              title={"Requirement Name"}
              value={reqNameSortDir}
              onChange={handleReqNameSortDir}
              width={300}
            />
          </div>
          <div className={classNames(styles.header, styles.status_header)}>
            <DashboardSortTableHeader
              key={`status-${statSortDir}`}
              title={"Status"}
              value={statSortDir}
              onChange={handleStatSortDir}
              width={150}
            />
          </div>
          <div className={styles.header}>
            <DashboardSortTableHeader
              key={`expir-${expirSortDir}`}
              title={"Expiration"}
              value={expirSortDir}
              onChange={handleExpirSortDir}
              width={200}
            />
          </div>
          <div className={classNames(styles.header, styles.status_header)}>
            <DashboardSortTableHeader
              key={`due-${dueSortDir}`}
              title={"Due Date"}
              value={dueSortDir}
              onChange={handleDueSortDir}
              width={200}
            />
          </div>
          <div className={classNames(styles.header, styles.status_header)}>
            <DashboardSortTableHeader
              key={`score-${scoreSortDir}`}
              title={"Score"}
              value={scoreSortDir}
              onChange={handleScoreSortDir}
              width={200}
            />
          </div>
          <div className={styles.header}>
            <DashboardSortTableHeader
              key={`type-${typeSortDir}`}
              title={"Type"}
              value={typeSortDir}
              onChange={handleTypeSortDir}
              width={200}
            />
          </div>
          <div className={styles.header}>
            <DashboardSortTableHeader
              key={`freq-${freqSortDir}`}
              title={"Frequency"}
              value={freqSortDir}
              onChange={handleFreqSortDir}
              width={200}
            />
          </div>
          <div className={styles.header}>
            <DashboardSortTableHeader
              key={`category-${categorySortDir}`}
              title={"Category"}
              value={categorySortDir}
              onChange={handleCategorySortDir}
              width={300}
            />
          </div>
          <div className={styles.header}>
            <DashboardSortTableHeader
              key={`rev-${revSortDir}`}
              title={"Reviewer"}
              value={revSortDir}
              onChange={handleRevSortDir}
              width={200}
            />
          </div>
        </div>

        <div className={styles.table_content}>
          {itemsToDisplay.length > 0 ? (
            itemsToDisplay.map((subItem, index) => {
              if (index % 2 === 0) {
                return (
                  <div className={styles.odd_row} key={subItem.id}>
                    <PassportRecordRequirementItem
                      item={subItem}
                      onClick={onItemClick}
                    />
                  </div>
                );
              } else {
                return (
                  <div className={styles.even_row} key={subItem.id}>
                    <PassportRecordRequirementItem
                      item={subItem}
                      onClick={onItemClick}
                    />
                  </div>
                );
              }
            })
          ) : (
            <div className={styles.nothing_to_display}>
              <p>No matching data.</p>
            </div>
          )}
        </div>

        {isLoading && (<div className={styles.loading}>
          <Loading />
        </div>)}
      </div>

      <div className={styles.table_pagination}>
        <DashboardTablePagination
          onPageChange={setCurrentPage}
          total={sortedItems.length}
          perPage={limit}
          currentPage={currentPage}
        />
      </div>
    </>
  );
}

PassportRecordRequirementTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      reqName: PropTypes.string.isRequired,
      src: PropTypes.string,
      freq: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      score: PropTypes.number,
      dueDate: PropTypes.string,
      expiration: PropTypes.string.isRequired,
      reviewer: PropTypes.string.isRequired,
      createdByTypeContact: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  onItemClick: PropTypes.func,
};
